import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import AppSidebar from '../components/AppSidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Plus, Search as SearchIcon, Pencil, Trash2, Briefcase, Mail, Phone,
  MapPin, FileSpreadsheet, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '', gstin: '', email: '', phone: '', address: '', state: '', notes: '',
};

const ClientsPage = () => {
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchClients = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = q ? `${API}/invoicing/clients?q=${encodeURIComponent(q)}` : `${API}/invoicing/clients`;
      const { data } = await axios.get(url);
      setClients(data.clients || []);
    } catch (err) {
      console.error('Failed to fetch clients', err);
      toast.error(err.response?.data?.detail || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => fetchClients(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, fetchClients]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '', gstin: c.gstin || '', email: c.email || '',
      phone: c.phone || '', address: c.address || '', state: c.state || '',
      notes: c.notes || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/invoicing/clients/${editing.client_id}`, form);
        toast.success('Client updated');
      } else {
        await axios.post(`${API}/invoicing/clients`, form);
        toast.success('Client created');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditing(null);
      fetchClients(query.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete client "${c.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/invoicing/clients/${c.client_id}`);
      toast.success('Client deleted');
      fetchClients(query.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <AppSidebar user={user} onLogout={logout} active="clients" />

      <main className="ml-72 p-8" data-testid="clients-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-[#1E3A8A]" />
              <span className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Billing &middot; Clients</span>
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Clients</h1>
            <p className="text-sm text-slate-500 mt-1">Companies and individuals you bill for legal &amp; tax services.</p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white shadow-md hover:shadow-lg"
            data-testid="new-client-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Client
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6 max-w-md">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, or GSTIN..."
            className="pl-10"
            data-testid="clients-search-input"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-48 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading clients...
          </div>
        ) : clients.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No clients yet</p>
              <p className="text-sm text-slate-400 mt-1">Add your first client to start issuing invoices.</p>
              <Button onClick={openCreate} className="mt-5" data-testid="empty-new-client-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Client
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {clients.map((c) => (
              <Card
                key={c.client_id}
                className="hover:shadow-lg transition-shadow border-slate-200"
                data-testid={`client-card-${c.client_id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {c.client_code && (
                          <span className="text-[10px] font-mono font-semibold bg-[#C9A84C]/15 text-[#8B7220] px-1.5 py-0.5 rounded uppercase tracking-wide"
                                data-testid={`client-code-${c.client_id}`}>
                            {c.client_code}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-base text-slate-900 truncate">{c.name}</CardTitle>
                      {c.gstin && (
                        <p className="text-xs text-slate-500 mt-1 font-mono">{c.gstin}</p>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)}
                              data-testid={`edit-client-${c.client_id}`}
                              className="h-8 w-8 hover:bg-slate-100">
                        <Pencil className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(c)}
                              data-testid={`delete-client-${c.client_id}`}
                              className="h-8 w-8 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-slate-600">
                  {c.email && (
                    <div className="flex items-center gap-2 truncate">
                      <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <a href={`mailto:${c.email}`} className="truncate hover:text-[#1E3A8A]">{c.email}</a>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>{c.phone}</span>
                    </div>
                  )}
                  {(c.address || c.state) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                      <span className="text-xs leading-relaxed">
                        {[c.address, c.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg" data-testid="client-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Client' : 'New Client'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Update the client details below.' : 'Add a new client you can invoice.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label htmlFor="cl-name">Name <span className="text-red-500">*</span></Label>
                <Input id="cl-name" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       required data-testid="client-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cl-gstin">GSTIN</Label>
                  <Input id="cl-gstin" value={form.gstin}
                         onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                         placeholder="22ABCDE1234F1Z5" data-testid="client-gstin-input" />
                </div>
                <div>
                  <Label htmlFor="cl-state">State</Label>
                  <Input id="cl-state" value={form.state}
                         onChange={(e) => setForm({ ...form, state: e.target.value })}
                         placeholder="Maharashtra" data-testid="client-state-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cl-email">Email</Label>
                  <Input id="cl-email" type="email" value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         data-testid="client-email-input" />
                </div>
                <div>
                  <Label htmlFor="cl-phone">Phone</Label>
                  <Input id="cl-phone" value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         data-testid="client-phone-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="cl-address">Address</Label>
                <Textarea id="cl-address" value={form.address}
                          onChange={(e) => setForm({ ...form, address: e.target.value })}
                          rows={2} data-testid="client-address-input" />
              </div>
              <div>
                <Label htmlFor="cl-notes">Internal Notes</Label>
                <Textarea id="cl-notes" value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          rows={2} data-testid="client-notes-input"
                          placeholder="Not shown on the invoice" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="save-client-btn"
                        className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white">
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editing ? 'Save Changes' : 'Create Client'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default ClientsPage;
